import { useEffect, useMemo } from 'react';

import { HARNESS_FLAGS } from '../../../api/harness/harnessFlags';
import useHarnessFlag from '../../../api/harness/useHarnessFlag';
import {
  ADA_JS_SUPPORT_SCRIPT,
  ADA_SCRIPT_ID,
  SESSION_STORAGE_TOKEN_KEY,
} from '../../../constants';
import {
  buildChatMetadata,
  isSensitiveChatMetadataKey,
  useChatMetadata,
  type ChatMetadataSession,
  type ChatMetadataState,
} from './chatMetadata';

type AdaMeta = {
  metaFields: Record<string, string | null>;
  sensitiveMetaFields: Record<string, string | null>;
};

const partitionMeta = (fields: Record<string, string | null>): AdaMeta => {
  const metaFields: Record<string, string | null> = {};
  const sensitiveMetaFields: Record<string, string | null> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (isSensitiveChatMetadataKey(key)) sensitiveMetaFields[key] = value;
    else metaFields[key] = value;
  }
  return { metaFields, sensitiveMetaFields };
};

export const buildAdaMetaFields = (
  state: ChatMetadataState,
  sessionValues: ChatMetadataSession,
): AdaMeta => partitionMeta(buildChatMetadata(state, sessionValues));

const useAdaMeta = (): AdaMeta => {
  const metadata = useChatMetadata();
  return useMemo(() => partitionMeta(metadata), [metadata]);
};

export const loadAda = (
  metaFields: Record<string, string | null>,
  sensitiveMetaFields: Record<string, string | null>,
) => {
  const existingScript = document.getElementById(ADA_SCRIPT_ID);

  if (existingScript) {
    return;
  }

  const script = document.createElement('script');
  script.src = ADA_JS_SUPPORT_SCRIPT;
  script.id = ADA_SCRIPT_ID;
  script.setAttribute('data-testid', ADA_SCRIPT_ID);
  script.dataset.handle = 'checkrcandidategr';
  window.adaSettings = {
    sensitiveMetaFields,
    metaFields,
    handle: 'checkr',
  };
  document.body.appendChild(script);
};

/**
 * Updates `window.adaSettings` and the live Ada embed with a new token.
 * Does NOT force a token refresh -- only propagates an already-refreshed value.
 */
export const refreshAdaToken = (token: string) => {
  if (window.adaSettings?.sensitiveMetaFields) {
    window.adaSettings.sensitiveMetaFields.cxUserAccessToken = token;
  }

  if (window.adaEmbed?.setSensitiveMetaFields) {
    window.adaEmbed
      .setSensitiveMetaFields(
        window.adaSettings?.sensitiveMetaFields || {
          cxUserAccessToken: token,
        },
      )
      .catch(() => {});
  }
};

/**
 * Reads the latest token from sessionStorage and pushes it to Ada.
 * Used by modal stop/start flows to ensure Ada restarts with a fresh token.
 */
export const refreshAdaTokenFromStorage = () => {
  const freshToken = window.sessionStorage.getItem(SESSION_STORAGE_TOKEN_KEY);
  if (freshToken) {
    refreshAdaToken(freshToken);
  }
};

export const useAda = (
  location: string,
  reportCount: number,
  enabled = true,
) => {
  const { enabled: adaForAllUsers } = useHarnessFlag({
    flagKey: HARNESS_FLAGS.ADA_ZERO_REPORT_USERS,
  });

  const { metaFields, sensitiveMetaFields } = useAdaMeta();

  useEffect(() => {
    if (
      enabled &&
      (reportCount > 0 || adaForAllUsers) &&
      !location.includes('/verification') &&
      !location.includes('/international')
    ) {
      loadAda(metaFields, sensitiveMetaFields);
    }
  }, [
    location,
    reportCount,
    enabled,
    adaForAllUsers,
    metaFields,
    sensitiveMetaFields,
  ]);

  useEffect(() => {
    if (!enabled) return;

    window.adaSettings = {
      ...window.adaSettings,
      metaFields,
      sensitiveMetaFields,
    };

    if (window.adaEmbed?.setMetaFields) {
      window.adaEmbed.setMetaFields(metaFields).catch(() => {});
    }
    if (window.adaEmbed?.setSensitiveMetaFields) {
      window.adaEmbed
        .setSensitiveMetaFields(sensitiveMetaFields)
        .catch(() => {});
    }
  }, [enabled, metaFields, sensitiveMetaFields]);
};
